import { useEffect, useState } from 'react'
import { api } from '@/api/client'
import type { HealthDb, SubmissionSummary } from '@/api/types'

/**
 * Office landing screen — shell only.
 *
 * Review, correction, and export control land in weeks 2–5. Today this proves
 * the office half of the codebase shares auth and the API client with the field
 * half, and surfaces the system health that accounting will actually care about.
 */
export function OfficeHome() {
  const [health, setHealth] = useState<HealthDb | null>(null)
  const [subs, setSubs] = useState<SubmissionSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const [h, s] = await Promise.all([api.healthDb(), api.submissions()])
        if (!cancelled) {
          setHealth(h)
          setSubs(s)
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Load failed')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="stack">
      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <h2>Sage connection</h2>
        {health === null ? (
          <p className="muted">Checking…</p>
        ) : (
          <>
            <p>
              Reading{' '}
              <span className="mono">{health.database}</span>{' '}
              <span className={`badge ${health.status === 'ok' ? 'badge-ok' : 'badge-error'}`}>
                {health.status === 'ok' ? 'Connected' : 'Degraded'}
              </span>
            </p>
            <p className="muted">
              Reference data reads live from Sage — nothing is cached, so nothing
              can be out of date.
            </p>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Reference view</th>
                    <th className="num">Records</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(health.views).map(([name, count]) => (
                    <tr key={name}>
                      <td>{name.replace(/_/g, ' ')}</td>
                      <td className="num mono">
                        {typeof count === 'number' ? count.toLocaleString() : count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div className="card">
        <h2>Crew submissions</h2>
        {subs === null ? (
          <p className="muted">Loading…</p>
        ) : subs.length === 0 ? (
          <p className="muted">
            No submissions yet. They appear here as soon as supervisors start
            recording crew days.
          </p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Job</th>
                  <th>Submitted by</th>
                  <th className="num">Crew</th>
                  <th className="num">Lines</th>
                  <th className="num">Hours</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.submission_id}>
                    <td>{s.date_worked}</td>
                    <td className="mono">{s.job_no}</td>
                    <td>{s.submitted_by ?? '—'}</td>
                    <td className="num">{s.crew_count}</td>
                    <td className="num">{s.line_count}</td>
                    <td className="num">{s.total_hours}</td>
                    <td>
                      <span
                        className={`badge ${
                          s.status === 'PostedToSage'
                            ? 'badge-ok'
                            : s.status === 'PostFailed'
                              ? 'badge-error'
                              : 'badge-info'
                        }`}
                      >
                        {s.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
